import { useIntl } from "react-intl";
import { FiCalendar } from "react-icons/fi";
import { RiMapPinLine, RiSurveyLine } from "react-icons/ri";
import {
  HomePageContainer,
  SectionSubHeader,
  SectionsContainer,
  SectionCard,
  HeaderWithIcon,
  ChecklistHeader,
  DatesContainer,
  SectionHeader,
  DatesDetails,
  DatesRow,
  ToDoImage,
  ChecklistContainer,
  ChecklistInfoTitle,
  ChecklistInfoDescription,
  ManualSectionCard,
  ManualTitle,
  ManualSubtitle,
  ActionButton,
  ManualIcons,
  RecomendedProductsCard,
  AcademyTitle,
  SectionRow,
  SectionHeaderTitle,
  AcademyIcons,
  ProductsAndProvidersTitle,
  RecomendedProvidersContainer,
  RecomendedProvidersCard,
  UserBox,
  ProviderCardContent,
  CardTitle,
  LocalizationLabel,
  ProviderInfo,
  PopoverFocus,
  PopoverContent,
  DrawerContent,
  DrawerLabel,
  ProviderInfoContainer,
  NoticeCard,
  NoticeCardContent,
  NavigationButton,
  FlagImage,
  FlagContainer,
  ServicesContainer,
  ManualLabel,
  DatesGrid,
} from "./styles";
import toDoImage from "./assets/to_do_image.png";
import manualIcons from "./assets/manual_icons.png";
import academyIcons from "./assets/academy_icons.png";
import { Drawer, Popover, Spin } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getHomepageEventInfoService,
  registerHomepageModuleAccessService,
} from "../../service/homePage.service";
import { markHomepageOrigin } from "../../utils/homepageAccessOrigin";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { AVAILABLE_ICONS } from "../../components/IconPickerModal";
import { FiUser } from "react-icons/fi";
import { IoLocationOutline } from "react-icons/io5";
import { RiInformationLine } from "react-icons/ri";
import { MdOutlineMail } from "react-icons/md";
import { IoIosInformationCircle } from "react-icons/io";
import useEventCode from "../../hooks/useEventCode";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import useProvisionTypes from "../../hooks/useProvisionTypes";
import ChecklistTable from "../Checklist/ChecklistTable";
import { loadLanguagePreference } from "../../redux/modules/language/actions";
import { loadUser } from "../../redux/modules/auth/actions";
import { DialogContext } from "../../hooks/dialog/context";
import ModalSettings from "../../newUXComponents/ModalSettings";
import useNotification from "../../hooks/newUXNotification";

const HomePage = () => {
  const intl = useIntl();

  const { event } = useSelector((state) => state.initialSettings);
  const eventId = event?.value;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const { accountCreated } = useSelector((state) => state.auth);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [hasChecklist, setHasChecklist] = useState(false);
  const eventCode = useEventCode();
  const { createDialog } = useContext(DialogContext);
  const [checklistSortParams, setChecklistSortParams] = useState({
    sortBy: null,
    sortDirection: null,
  });
  const dispatch = useDispatch();
  const notification = useNotification();

  useEffect(() => {
    if (accountCreated) {
      if (!event || event.length === 0 || JSON.stringify(event) === "{}") {
        notification.close("event-not-selected-message");
        notification.displayInfo(
          intl.formatMessage({ id: "error.contract_not_selected" }),
          "event-not-selected-message"
        );
        dispatch(loadUser());
        dispatch(loadLanguagePreference());

        createDialog({
          id: "modal-settings-login",
          Component: ModalSettings,
          props: {},
        });
        return;
      } else notification.close("event-not-selected-message");
    }
  }, [accountCreated, event]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const params =
        checklistSortParams.sortBy && checklistSortParams.sortDirection
          ? {
              sortBy: checklistSortParams.sortBy,
              sortDirection: checklistSortParams.sortDirection,
            }
          : {};

      const response = await getHomepageEventInfoService(eventId, params);
      const checklist = response.data?.checklist;

      setData(response.data);
      setHasChecklist(!!checklist?.items?.length);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({
          id: "error",
        }),
        description: intl.formatMessage({
          id: "api.error." + error.response?.data?.code,
        }),
      });
    } finally {
      setLoading(false);
    }
  }, [eventId, checklistSortParams, intl]);

  useEffect(() => {
    if (!eventId) return;

    fetchData();
  }, [eventId, fetchData]);

  const formatDate = (date) => {
    if (!date) return "-";

    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  const handleOpenDrawer = (provider) => {
    setSelectedProvider(provider);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedProvider(null);
  };

  function getCountryCode(phone) {
    if (!phone) return null;

    const phoneNumber = parsePhoneNumberFromString(phone);

    return phoneNumber ? phoneNumber.country : null;
  }

  function Flag({ country }) {
    if (!country) return null;

    return (
      <FlagImage
        src={`https://flagcdn.com/w80/${country.toLowerCase()}.png`}
        alt={country}
      />
    );
  }

  const countryPhone = getCountryCode(selectedProvider?.phone);
  const countryMobile = getCountryCode(selectedProvider?.mobile);

  const { provisionTypes } = useProvisionTypes();
  const provisionMap = provisionTypes.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});

  const items = data?.checklist?.items ?? [];

  const handleChecklistTableChange = (params) => {
    if (!params.sortBy || !params.sortDirection) {
      setChecklistSortParams({
        sortBy: null,
        sortDirection: null,
      });
      return;
    }

    setChecklistSortParams({
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
    });
  };

  const handleModuleAccess = useCallback(
    (module, path, { logOnDestination = false } = {}) => {
      if (logOnDestination) {
        markHomepageOrigin(module);
      } else if (eventId) {
        registerHomepageModuleAccessService(eventId, module).catch((error) => {
          console.error("Erro ao registrar log de acesso da homepage:", error);
        });
      }

      history.push(path);
    },
    [eventId, history],
  );

  return (
    <HomePageContainer>
      <Spin spinning={loading} fullscreen>
        <SectionsContainer>
          {data?.config?.showImportantDates !== false && (
            <DatesContainer>
              <SectionCard>
                <HeaderWithIcon>
                  <FiCalendar size={22} color="rgba(17, 167, 217, 1)" />
                  <SectionHeaderTitle>
                    {intl.formatMessage({ id: "important_dates" })}
                  </SectionHeaderTitle>
                </HeaderWithIcon>

                <DatesGrid>
                  {Object.entries(data?.importantDates || {})
                    .map(([key, value]) => {
                    if (key === "paymentDeadline") {
                      return (
                        <div key={key}>
                          <SectionSubHeader>
                            {intl.formatMessage({ id: "paymentDeadline" })}
                          </SectionSubHeader>

                          <DatesRow>
                            <DatesDetails>
                              <span>
                                {intl.formatMessage({ id: "deadline" })}
                              </span>
                              <strong>{formatDate(value)}</strong>
                            </DatesDetails>
                          </DatesRow>
                        </div>
                      );
                    }

                    return (
                      <div key={key}>
                        <SectionSubHeader>
                          {intl.formatMessage({ id: key })}
                        </SectionSubHeader>

                        <DatesRow>
                          <DatesDetails>
                            <span>{intl.formatMessage({ id: "opening" })}</span>
                            <strong>{formatDate(value?.startDate)}</strong>
                          </DatesDetails>

                          <DatesDetails>
                            <span>{intl.formatMessage({ id: "closing" })}</span>
                            <strong>{formatDate(value?.endDate)}</strong>
                          </DatesDetails>
                        </DatesRow>
                        {value?.deadline && (
                          <DatesDetails style={{ marginTop: 8 }}>
                            <span>
                              {intl.formatMessage({ id: "deadline" })}
                            </span>
                            <strong>{formatDate(value?.deadline)}</strong>
                          </DatesDetails>
                        )}
                      </div>
                    );
                  })}
                </DatesGrid>
              </SectionCard>
            </DatesContainer>
          )}
          <div style={{ width: "100%" }}>
            <SectionCard hasManual={data?.config?.showManual}>
              <ChecklistHeader>
                <HeaderWithIcon>
                  <RiSurveyLine size={22} color="rgba(17, 167, 217, 1)" />
                  <SectionHeaderTitle>Checklist</SectionHeaderTitle>
                </HeaderWithIcon>

                <ActionButton
                  type="primary"
                  onClick={() => handleModuleAccess("CHECKLIST", "/checklist")}
                  disabled={!hasChecklist}
                >
                  {intl.formatMessage({ id: "view_checklist" })}
                </ActionButton>
              </ChecklistHeader>
              <ChecklistContainer hasChecklist>
                {hasChecklist ? (
                  <ChecklistTable
                    isHomepage
                    data-test="checklist-table-on-home"
                    items={items?.slice(0, data?.config?.showManual ? 3 : 6)}
                    onTableChange={handleChecklistTableChange}
                    onStatusChanged={fetchData}
                  />
                ) : (
                  <>
                    <ToDoImage src={toDoImage} alt="To Do Image" />
                    <ChecklistInfoTitle>
                      {intl.formatMessage({ id: "checklist_preparation" })}
                    </ChecklistInfoTitle>
                    <ChecklistInfoDescription>
                      {intl.formatMessage({
                        id: "checklist_preparation_sub",
                      })}
                    </ChecklistInfoDescription>
                  </>
                )}
              </ChecklistContainer>
            </SectionCard>
            {data?.config?.showManual && (
              <ManualSectionCard>
                <div>
                  <ManualLabel>
                    {intl.formatMessage({ id: "event_edition" })}
                  </ManualLabel>
                  <ManualTitle>
                    {intl.formatMessage({ id: "exhibitor_manual" })}
                  </ManualTitle>
                  <ManualSubtitle>
                    {intl.formatMessage({ id: "homepage_manual_subtitle" })}
                  </ManualSubtitle>
                  <ActionButton
                    type="primary"
                    onClick={() =>
                      handleModuleAccess("MANUAL", `/event-manual/${eventCode}`)
                    }
                  >
                    {intl.formatMessage({ id: "know_anual" })}
                  </ActionButton>
                </div>

                <ManualIcons src={manualIcons} alt="Manual Icons" />
              </ManualSectionCard>
            )}
          </div>
        </SectionsContainer>

        {data?.config?.showEcommerceProducts && (
          <>
            <SectionHeader>
              <div>
                <ProductsAndProvidersTitle>
                  {intl.formatMessage({ id: "recommended_products" })}
                </ProductsAndProvidersTitle>
                <p>
                  {intl.formatMessage({
                    id: "recommended_products_description",
                  })}
                </p>
              </div>
              <ActionButton
                type="primary"
                onClick={() =>
                  handleModuleAccess("ECOMMERCE", "/ecommerce", {
                    logOnDestination: true,
                  })
                }
              >
                {intl.formatMessage({ id: "recommended_products_access" })}
              </ActionButton>
            </SectionHeader>
            <SectionRow products>
              {data?.ecommerceProducts?.map((item, index) => {
                const iconData = AVAILABLE_ICONS.find(
                  (i) => i.name === item.icon
                );

                const Icon = iconData?.component;

                return (
                  <RecomendedProductsCard key={index}>
                    {Icon && <Icon size={34} color={"rgba(0, 34, 68, 1)"} />}

                    <CardTitle>{item.name}</CardTitle>
                  </RecomendedProductsCard>
                );
              })}
            </SectionRow>
          </>
        )}

        {data?.config?.showAcademy && (
          <SectionRow academy>
            <div style={{ width: "551px" }}>
              <AcademyTitle>
                {intl.formatMessage({ id: "informa_academy" })}
              </AcademyTitle>
              <p>{intl.formatMessage({ id: "academy_description" })}</p>
              <ActionButton
                type="primary"
                onClick={() =>
                  handleModuleAccess("ACADEMY", `/informa-academy/${eventCode}`)
                }
              >
                {intl.formatMessage({ id: "academy_discover" })}
              </ActionButton>
            </div>
            <AcademyIcons src={academyIcons} alt="Academy Icons" />
          </SectionRow>
        )}

        {data?.config?.showServiceProviders && (
          <>
            <div style={{ marginTop: "40px" }}>
              <ProductsAndProvidersTitle>
                {intl.formatMessage({ id: "recommended_providers" })}
              </ProductsAndProvidersTitle>
              <p>
                {intl.formatMessage({
                  id: "recommended_providers_description",
                })}
              </p>
            </div>
            <RecomendedProvidersContainer>
              {data?.serviceProviders.map((provider) => {
                const { id, name, services, address } = provider;
                const { district, state } = address;

                const visibleServices = services.slice(0, 2);
                const hiddenServices = services.slice(2);

                return (
                  <RecomendedProvidersCard
                    onClick={() => {
                      handleOpenDrawer(provider);
                    }}
                    key={id}
                  >
                    <UserBox>
                      <FiUser size={21} color={"rgba(17, 167, 217, 1)"} />
                    </UserBox>

                    <ProviderCardContent>
                      <CardTitle>{name}</CardTitle>

                      <LocalizationLabel>
                        <IoLocationOutline size={12} />
                        {district}, {state}
                      </LocalizationLabel>

                      <ServicesContainer>
                        {visibleServices.map((service, index) => (
                          <ProviderInfo key={index}>
                            {provisionMap[service] || service}
                          </ProviderInfo>
                        ))}

                        {hiddenServices.length > 0 && (
                          <Popover
                            content={
                              <PopoverContent>
                                {hiddenServices.map((service, index) => (
                                  <ProviderInfo
                                    moreThanOne={data?.serviceProviders > 1}
                                    key={index}
                                  >
                                    {provisionMap[service] || service}
                                  </ProviderInfo>
                                ))}
                              </PopoverContent>
                            }
                            trigger="hover"
                            placement="top"
                            overlayInnerStyle={{ padding: 10 }}
                          >
                            <PopoverFocus>
                              +{hiddenServices.length}
                            </PopoverFocus>
                          </Popover>
                        )}
                      </ServicesContainer>
                    </ProviderCardContent>
                  </RecomendedProvidersCard>
                );
              })}
            </RecomendedProvidersContainer>
          </>
        )}
      </Spin>

      <Drawer
        title={selectedProvider?.name}
        placement="right"
        onClose={handleCloseDrawer}
        visible={isDrawerOpen}
        width={400}
      >
        {selectedProvider && (
          <DrawerContent>
            <HeaderWithIcon>
              <RiInformationLine size={22} color="rgba(17, 167, 217, 1)" />
              <SectionHeaderTitle>
                {intl.formatMessage({ id: "recommended_providers_about" })}
              </SectionHeaderTitle>
            </HeaderWithIcon>

            <DrawerLabel>
              {intl.formatMessage({ id: "recommended_providers_services" })}
            </DrawerLabel>
            <ProviderInfoContainer>
              {selectedProvider.services.map((service, index) => (
                <ProviderInfo key={index}>
                  {provisionMap[service] || service}
                </ProviderInfo>
              ))}
            </ProviderInfoContainer>

            <DrawerLabel>
              {intl.formatMessage({ id: "tax_classification" })}
            </DrawerLabel>
            <span>{selectedProvider?.fiscalClassification}</span>

            <HeaderWithIcon>
              <MdOutlineMail size={22} color="rgba(17, 167, 217, 1)" />
              <SectionHeaderTitle>
                {intl.formatMessage({ id: "contact" })}
              </SectionHeaderTitle>
            </HeaderWithIcon>

            <DrawerLabel>{intl.formatMessage({ id: "telephone" })}</DrawerLabel>
            <span>{intl.formatMessage({ id: "commercial" })}</span>
            <FlagContainer>
              <Flag country={countryPhone} />
              <span>{selectedProvider?.phone || "-"}</span>
            </FlagContainer>

            <span>{intl.formatMessage({ id: "cell_phone" })}</span>
            <FlagContainer>
              <Flag country={countryMobile} />
              <span>{selectedProvider?.mobile || "-"}</span>
            </FlagContainer>

            <DrawerLabel>{intl.formatMessage({ id: "email" })}</DrawerLabel>
            <span>{selectedProvider?.email}</span>

            <HeaderWithIcon>
              <RiMapPinLine size={22} color="rgba(17, 167, 217, 1)" />
              <SectionHeaderTitle>
                {intl.formatMessage({ id: "address" })}
              </SectionHeaderTitle>
            </HeaderWithIcon>
            <span>
              {selectedProvider?.address.avenue},{" "}
              {selectedProvider?.address.number}
              <br />
              {selectedProvider?.address.district},{" "}
              {selectedProvider?.address.city} -{" "}
              {selectedProvider?.address.state},{" "}
              {selectedProvider?.address.country} <br />
              {selectedProvider?.address.zipCode}
            </span>

            <NoticeCard>
              <IoIosInformationCircle size={21} color="rgba(17, 167, 217, 1)" />
              <NoticeCardContent>
                <span style={{ fontSize: 16 }}>
                  {intl.formatMessage({ id: "recommended_providers_hire" })}
                </span>
                <span style={{ fontSize: 14 }}>
                  {intl.formatMessage({
                    id: "recommended_providers_associate_message",
                  })}
                </span>
              </NoticeCardContent>
            </NoticeCard>

            <NavigationButton
              onClick={() => {
                history.push("/associate-company/new", {
                  document: selectedProvider?.document,
                });
              }}
            >
              {intl.formatMessage({ id: "recommended_providers_associate" })}
            </NavigationButton>
          </DrawerContent>
        )}
      </Drawer>
    </HomePageContainer>
  );
};

export default HomePage;
